import type { ButtonHTMLAttributes } from 'react'
import { Link } from 'react-router'
import { cx } from '../cx'
import styles from './IconButton.module.css'

export type IconName =
  | 'add'
  | 'edit'
  | 'close'
  | 'gallery'
  | 'prev'
  | 'next'
  | 'download'
  | 'delete'
  | 'main'
  | 'help'
  | 'info'
  | 'top'
  | 'refresh'
  | 'print'
  | 'hide'
  | 'show'
  | 'dead'
  | 'more'
  | 'clone'

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'children'
> {
  /** The accessible name. Required — an icon on its own says nothing to a screen reader. */
  label: string
  icon: IconName
  className?: string | undefined
}

/**
 * A square, icon-only control for a card header (spec §2: 44px minimum hit area).
 *
 * It exists so a section's add or edit action can sit beside the section's name instead of
 * under its content. A full-width button at the foot of a card is easy to reach on a short
 * card and invisible on a long one — the aspects card put "Edit aspects" below nine chips,
 * and the rote-spell card put "Add rote spell" below four spells and their tags.
 */
export function IconButton({ label, icon, className, type = 'button', ...rest }: IconButtonProps) {
  return (
    <button
      type={type}
      className={cx(styles.btn, className)}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Glyph icon={icon} />
    </button>
  )
}

export interface IconLinkProps {
  label: string
  icon: IconName
  /** Router destination. */
  to: string
  className?: string | undefined
}

/**
 * The same control, when the action is "go somewhere" rather than "do something".
 *
 * An anchor, not a button with `navigate()`: a destination should survive a middle-click, an
 * open-in-new-tab and a long-press, and screen readers announce a link as a link.
 */
export function IconLink({ label, icon, to, className }: IconLinkProps) {
  return (
    <Link to={to} className={cx(styles.btn, className)} aria-label={label} title={label}>
      <Glyph icon={icon} />
    </Link>
  )
}

function Glyph({ icon }: { icon: IconName }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {icon === 'add' && <path d="M12 5v14M5 12h14" />}
      {icon === 'edit' && <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3zM14 7l3 3" />}
      {icon === 'close' && <path d="M6 6l12 12M18 6L6 18" />}
      {icon === 'prev' && <path d="M15 5l-7 7 7 7" />}
      {icon === 'next' && <path d="M9 5l7 7-7 7" />}
      {icon === 'download' && <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19h14" />}
      {/* An arrow to a ceiling: "back to the top of this page", not "upload". */}
      {icon === 'top' && <path d="M12 20V7M7 12l5-5 5 5M6 4h12" />}
      {/* A loop closing on itself: something spent is put back the way it was. */}
      {icon === 'refresh' && <path d="M20 12a8 8 0 1 1-2.3-5.6M20 4v4h-4" />}
      {/* A sheet feeding through a printer: the page above, the machine around it, the
          printed copy below. Not a document glyph — this action produces paper. */}
      {icon === 'print' && (
        <>
          <path d="M7 9V4h10v5" />
          <path d="M5 9h14a2 2 0 0 1 2 2v5h-4M7 16H3v-5a2 2 0 0 1 2-2" />
          <rect x="7" y="14" width="10" height="6" rx="1" />
        </>
      )}
      {/* A trash can, not an close cross: this destroys a stored file, and the close glyph
          already means "put this away". */}
      {icon === 'delete' && (
        <>
          <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
          <path d="M10 11v6M14 11v6" />
        </>
      )}
      {/* A star: the main image is the one every sheet renders, which is a promotion rather
          than a checkbox. */}
      {icon === 'main' && (
        <path d="M12 4.5l2.35 4.76 5.25.76-3.8 3.7.9 5.23L12 16.47l-4.7 2.47.9-5.22-3.8-3.71 5.25-.76z" />
      )}
      {icon === 'help' && (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.6 9.2a2.5 2.5 0 1 1 3.2 2.9c-.5.2-.8.7-.8 1.3v.4" />
          <path d="M12 17.1v.01" />
        </>
      )}
      {/* A lowercase i in a ring: "there is more to read about this one", where `help`
          asks a question about the app itself. */}
      {icon === 'info' && (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5.5" />
          <path d="M12 7.6v.01" />
        </>
      )}
      {/* An eye with a stroke through it. "Placed, but the players are not sent it" — which is a
          different claim from `close`, and the two appear side by side on the live-session tools. */}
      {icon === 'hide' && (
        <>
          <path d="M3 12s3.6-6 9-6 9 6 9 6-3.6 6-9 6-9-6-9-6z" />
          <circle cx="12" cy="12" r="2.4" />
          <path d="M4 20 20 4" />
        </>
      )}
      {/* The same eye, open. */}
      {icon === 'show' && (
        <>
          <path d="M3 12s3.6-6 9-6 9 6 9 6-3.6 6-9 6-9-6-9-6z" />
          <circle cx="12" cy="12" r="2.4" />
        </>
      )}
      {/* A skull. Blunt on purpose: this marks someone out of a fight, and it sits next to three
          other glyphs that all mean smaller things. */}
      {icon === 'dead' && (
        <>
          <path d="M12 3a7 7 0 0 0-7 7v3.2c0 .8.5 1.6 1.3 1.9l.7.3V19a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-3.6l.7-.3c.8-.3 1.3-1.1 1.3-1.9V10a7 7 0 0 0-7-7z" />
          <circle cx="9.3" cy="11" r="1.4" />
          <circle cx="14.7" cy="11" r="1.4" />
        </>
      )}
      {/* Three dots: the overflow menu. Deliberately the least interesting glyph in the set —
          it opens a list of actions rather than performing one. */}
      {icon === 'more' && (
        <>
          <circle cx="12" cy="5.5" r="1.2" fill="currentColor" />
          <circle cx="12" cy="12" r="1.2" fill="currentColor" />
          <circle cx="12" cy="18.5" r="1.2" fill="currentColor" />
        </>
      )}
      {/* Two sheets, one behind the other: "another one of these". Not `add`, which already
          means "put this on the map" one glyph along in the same row — and this is not adding
          somebody to the encounter but taking a second copy of somebody already in it. */}
      {icon === 'clone' && (
        <>
          <rect x="9" y="9" width="11" height="11" rx="2" />
          <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
        </>
      )}
      {icon === 'gallery' && (
        <>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <circle cx="8.5" cy="10" r="1.4" />
          <path d="M20.5 15.5 16 11l-5.5 6" />
        </>
      )}
    </svg>
  )
}
